import React from 'react';
import type { NextPageWithLayout, GetServerSideProps } from 'next';
import { Op, OrderBy } from 'contensis-delivery-api';
import contensis from 'Common/api/contensis';
import cmsParser from 'Common/util/cmsParser';
import setCacheHeaders from 'Common/func/setCacheHeaders';
import { excludeMetadata } from 'Common/util/contensis/api';

import MainLayout from 'Layout/Main';
import Metadata from 'Components/Metadata';
import Template, { datamap, PageData } from 'Templates/OpenDays/Landing';

const OpenDaysLanding: NextPageWithLayout<PageData> = ({ entry, upcomingOpenDays, ancestors }) => {
    return (
        <>
            <Metadata {...cmsParser.metadata(entry)} />
            <Template {...datamap({ entry, upcomingOpenDays, ancestors })} />
        </>
    );
};

OpenDaysLanding.getLayout = (page, props) => {
    return <MainLayout {...props}>{page}</MainLayout>;
};

export const getServerSideProps: GetServerSideProps<any> = async ({ preview, res }) => {
    const { fetch, surrogateKeys } = contensis({ preview, fetchSurrogateKeys: true });
    const { data, error } = await fetch(async (client, search) => {
        const resPage = await client.nodes.get({
            path: '/open-days',
            entryLinkDepth: 1,
            entryFields: ['*', ...excludeMetadata]
        });

        if (!resPage.entry) {
            return { error: true };
        }

        const entry = resPage.entry;
        const ancestors = await client.nodes.getAncestors({
            id: resPage.id,
            entryFields: ['title']
        });

        const eventsQuery = [
            Op.or(
                Op.equalTo('sys.contentTypeId', 'eventUndergraduateOpenDay'),
                Op.equalTo('sys.contentTypeId', 'eventPostgraduateOpenDay'),
                Op.equalTo('sys.contentTypeId', 'eventCadetshipsOpenDay'),
                Op.equalTo('sys.contentTypeId', 'eventApprenticeshipsOpenDay'),
                Op.equalTo('sys.contentTypeId', 'eventApplicantTasterDays'),
                Op.equalTo('sys.contentTypeId', 'eventClearingOpenDay')
            ),
            Op.greaterThan('date.from', new Date())
        ];

        const resEvents = await search(eventsQuery, {
            fields: [
                'bookingLink.entryTitle',
                'date.from',
                'eventType',
                'delivery',
                'sys.id',
                'sys.contentTypeId'
            ],
            orderBy: OrderBy.asc('date.from')
        });

        return {
            entry,
            upcomingOpenDays: resEvents.items,
            ancestors
        };
    });

    if (error) {
        return {
            notFound: true
        };
    } else {
        setCacheHeaders(res, surrogateKeys);

        return {
            props: data
        };
    }
};

export default OpenDaysLanding;
